import React, { useState, useCallback } from "react";
import client from "@/api/client";
import { toast } from "sonner";
import { Shield, AlertTriangle, CheckCircle2, RefreshCw, ChevronDown, ChevronRight, Activity, Check, ArrowRight, Lock } from "lucide-react";

const TIER_CONFIG = {
  excellent: { color: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Excellent" },
  good:      { color: "text-blue-700 bg-blue-50 border-blue-200",     label: "Good" },
  moderate:  { color: "text-amber-700 bg-amber-50 border-amber-200",  label: "Moderate" },
  low:       { color: "text-orange-700 bg-orange-50 border-orange-200", label: "Low" },
  very_low:  { color: "text-red-700 bg-red-50 border-red-200",        label: "Very Low" },
};

function ScoreGauge({ score }) {
  const pct = Math.min(100, Math.max(0, score || 0));
  const color = pct >= 75 ? "#059669" : pct >= 60 ? "#3B82F6" : pct >= 40 ? "#F59E0B" : "#EF4444";
  const circumference = 2 * Math.PI * 36;
  const strokeDash = (pct / 100) * circumference;
  return (
    <div className="flex flex-col items-center">
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="36" fill="none" stroke="#E2E8F0" strokeWidth="10" />
        <circle cx="50" cy="50" r="36" fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${strokeDash} ${circumference}`}
          strokeDashoffset={circumference * 0.25}
          strokeLinecap="round" style={{ transition: "stroke-dasharray 0.8s ease" }} />
        <text x="50" y="52" textAnchor="middle" dominantBaseline="middle"
          className="font-bold" style={{ fill: "#0F172A", fontSize: 18, fontWeight: 700 }}>
          {pct.toFixed(0)}
        </text>
        <text x="50" y="65" textAnchor="middle" style={{ fill: "#94A3B8", fontSize: 9 }}>/ 100</text>
      </svg>
    </div>
  );
}

function ComponentBar({ label, score, weight }) {
  const pct = Math.min(100, Math.max(0, score || 0));
  const color = pct >= 75 ? "bg-emerald-500" : pct >= 60 ? "bg-blue-500" : pct >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-3">
      <div className="w-36 flex-shrink-0">
        <p className="text-xs text-slate-600">{label}</p>
        <p className="text-xs text-slate-400">{weight}</p>
      </div>
      <div className="flex-1 bg-slate-100 rounded-full h-2">
        <div className={`h-2 rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-slate-700 w-10 text-right">{pct.toFixed(0)}</span>
    </div>
  );
}

// ── Verification Ladder Component ─────────────────────────────────────────
const VERIF_STEPS = [
  {
    key: "self_reported",
    label: "Self-Reported",
    desc: "Data submitted by organization. Not yet reviewed.",
    unlocksLabel: null,
    color: "#94A3B8",
  },
  {
    key: "system_validated",
    label: "System Validated",
    desc: "Automated checks passed. Suitable for standard FI review.",
    unlocksLabel: "Unlocks: Standard FI financing review",
    color: "#3B82F6",
  },
  {
    key: "analyst_reviewed",
    label: "Analyst Reviewed",
    desc: "Reviewed by DeCarbonIQ analyst. Higher institutional credibility.",
    unlocksLabel: "Unlocks: Expedited FI screening + analyst certificate",
    color: "#6366F1",
  },
  {
    key: "third_party_verified",
    label: "Third-Party Verified",
    desc: "Independently audited. Maximum institutional trust.",
    unlocksLabel: "Unlocks: Highest-tier climate credential",
    color: "#059669",
  },
];

function VerificationLadder({ assessmentId, currentScore }) {
  const [verif,      setVerif]      = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadVerif = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`/confidence/assessments/${assessmentId}/verification-status`);
      setVerif(res.data);
    } catch {}
    setLoading(false);
  }, [assessmentId]);

  React.useEffect(() => { loadVerif(); }, [loadVerif]);

  const submitForReview = async () => {
    setSubmitting(true);
    try {
      const res = await client.post(`/confidence/assessments/${assessmentId}/submit-for-review`);
      toast.success(res.data.message || "Submitted for system validation.");
      loadVerif();
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); }
    setSubmitting(false);
  };

  const currentIdx = VERIF_STEPS.findIndex(s => s.key === (verif?.verification_status || "self_reported"));
  const isSelfReported = (verif?.verification_status || "self_reported") === "self_reported";
  const isTerminal     = verif?.verification_status === "third_party_verified";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="verification-ladder">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
          <Shield size={14} className="text-[#059669]" /> Verification Status
        </h3>
        <button onClick={loadVerif} className="text-slate-400 hover:text-slate-600">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Current status banner */}
      {verif && (
        <div className="mb-4 px-3 py-2.5 rounded-xl border" style={{
          backgroundColor: `${VERIF_STEPS[currentIdx]?.color}12`,
          borderColor: `${VERIF_STEPS[currentIdx]?.color}40`,
        }}>
          <p className="text-xs font-semibold" style={{ color: VERIF_STEPS[currentIdx]?.color }}>
            Current: {VERIF_STEPS[currentIdx]?.label}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">{verif.label}</p>
        </div>
      )}

      {/* Step ladder */}
      <div className="space-y-2 mb-4" data-testid="verif-steps">
        {VERIF_STEPS.map((step, i) => {
          const done    = i <= currentIdx;
          const current = i === currentIdx;
          return (
            <div key={step.key} className={`flex items-start gap-3 p-3 rounded-xl transition-all
              ${current ? "bg-slate-50 border border-slate-200" : ""}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
                ${done ? "text-white" : "bg-slate-100 text-slate-400"}`}
                style={done ? { backgroundColor: step.color } : {}}>
                {done ? <Check size={12} strokeWidth={3} /> : <span className="text-[10px] font-bold">{i + 1}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold ${done ? "text-[#0F172A]" : "text-slate-400"}`}>{step.label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{step.desc}</p>
                {step.unlocksLabel && (
                  <p className="text-[10px] mt-1 flex items-center gap-1" style={{ color: done ? step.color : "#CBD5E1" }}>
                    {done ? <Check size={10} /> : <Lock size={10} />}
                    {step.unlocksLabel}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      {isSelfReported && !isTerminal && (
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-500 mb-3">
            Run automated validation checks to upgrade your verification status.
            System Validated status improves your eligibility for FI financing review.
          </p>
          <button onClick={submitForReview} disabled={submitting || !currentScore}
            data-testid="submit-for-validation-btn"
            className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#0F172A] text-white text-xs font-medium rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors">
            {submitting ? <RefreshCw size={12} className="animate-spin" /> : <ArrowRight size={12} />}
            {submitting ? "Submitting..." : "Submit for System Validation"}
          </button>
          {!currentScore && (
            <p className="text-[10px] text-slate-400 text-center mt-2">
              Compute your confidence score first to enable submission.
            </p>
          )}
        </div>
      )}
      {!isSelfReported && !isTerminal && (
        <div className="border-t border-slate-100 pt-4 text-center">
          <p className="text-xs text-slate-400">{verif?.next_step}</p>
          <p className="text-[10px] text-slate-300 mt-1">Contact support to upgrade to Analyst Review.</p>
        </div>
      )}
      {isTerminal && (
        <div className="border-t border-slate-100 pt-4 flex items-center gap-2 justify-center text-xs text-emerald-600">
          <CheckCircle2 size={14} /> Maximum verification level achieved.
        </div>
      )}
    </div>
  );
}

export default function IntelligencePanel({ assessmentId }) {
  const [score, setScore] = useState(null);
  const [validation, setValidation] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState({ score: false, validate: false, anomalies: false });
  const [showIssues, setShowIssues] = useState(false);

  const loadScore = useCallback(async () => {
    setLoading(l => ({ ...l, score: true }));
    try {
      const [sRes, eRes] = await Promise.all([
        client.get(`/confidence/assessments/${assessmentId}/score`),
        client.get(`/confidence/assessments/${assessmentId}/evidence-coverage`),
      ]);
      setScore(sRes.data);
      setEvidence(eRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to compute confidence score"); }
    setLoading(l => ({ ...l, score: false }));
  }, [assessmentId]);

  const runValidation = useCallback(async () => {
    setLoading(l => ({ ...l, validate: true }));
    try {
      const res = await client.post(`/confidence/assessments/${assessmentId}/validate`);
      setValidation(res.data);
      toast.success(res.data.is_valid ? "Validation passed — no critical issues." : `Validation found ${res.data.issue_count} issue(s).`);
    } catch (err) { toast.error(err.response?.data?.detail || "Validation failed"); }
    setLoading(l => ({ ...l, validate: false }));
  }, [assessmentId]);

  const detectAnomalies = useCallback(async () => {
    setLoading(l => ({ ...l, anomalies: true }));
    try {
      const res = await client.get(`/confidence/assessments/${assessmentId}/anomalies`);
      setAnomalies(res.data);
    } catch (err) { toast.error("Anomaly detection failed"); }
    setLoading(l => ({ ...l, anomalies: false }));
  }, [assessmentId]);

  const tierCfg = TIER_CONFIG[score?.tier] || TIER_CONFIG.low;

  return (
    <div className="space-y-4">
      {/* Score Panel */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-[#059669]" />
            <h3 className="text-sm font-semibold text-[#0F172A]">Data Confidence Score</h3>
          </div>
          <button onClick={loadScore} disabled={loading.score} data-testid="compute-confidence-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-60">
            <RefreshCw size={12} className={loading.score ? "animate-spin" : ""} />
            {score ? "Recompute" : "Compute Score"}
          </button>
        </div>

        {score ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center justify-center">
              <ScoreGauge score={score.overall_score} />
              <span className={`mt-2 text-xs font-semibold px-2 py-1 rounded-full border ${tierCfg.color}`} data-testid="confidence-tier">
                {tierCfg.label}
              </span>
              <p className="text-xs text-slate-400 mt-1 text-center">{score.tier_label}</p>
            </div>
            <div className="col-span-2 space-y-3">
              {[
                { label: "Data Completeness", key: "completeness", weight: "25%" },
                { label: "Evidence Coverage", key: "evidence_score", weight: "25%" },
                { label: "Data Consistency", key: "consistency", weight: "20%" },
                { label: "Factor Source Quality", key: "source_reliability", weight: "15%" },
                { label: "Sector Benchmark Fit", key: "benchmark_fit", weight: "15%" },
              ].map(({ label, key, weight }) => (
                <ComponentBar key={key} label={label} score={score[key]} weight={weight} />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <Shield size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Click "Compute Score" to analyse data confidence.</p>
          </div>
        )}
      </div>

      {/* Evidence Coverage */}
      {evidence && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
            <Activity size={14} className="text-[#059669]" /> Evidence Coverage
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Activity Coverage", value: `${evidence.activity_coverage_pct}%` },
              { label: "Verified Evidence", value: `${evidence.verified_coverage_pct}%` },
              { label: "Emission Coverage", value: `${evidence.emission_coverage_pct}%` },
            ].map(({ label, value }) => (
              <div key={label} className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-400">{label}</p>
                <p className="text-lg font-bold text-[#0F172A]">{value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validation */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
            <CheckCircle2 size={14} className="text-[#059669]" /> Input Validation
          </h3>
          <button onClick={runValidation} disabled={loading.validate} data-testid="run-validation-btn"
            className="flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 text-slate-600 text-xs rounded-lg hover:border-slate-300 disabled:opacity-60">
            <RefreshCw size={11} className={loading.validate ? "animate-spin" : ""} /> Run Validation
          </button>
        </div>
        {validation ? (
          <div>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${validation.is_valid ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {validation.is_valid
                ? <><CheckCircle2 size={14} /> All checks passed — {validation.warning_count} warning(s)</>
                : <><AlertTriangle size={14} /> {validation.issue_count} issue(s), {validation.warning_count} warning(s)</>
              }
            </div>
            {(validation.issues?.length > 0 || validation.warnings?.length > 0) && (
              <div className="mt-2">
                <button onClick={() => setShowIssues(!showIssues)}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
                  {showIssues ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {showIssues ? "Hide" : "Show"} details
                </button>
                {showIssues && (
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                    {[...(validation.issues || []), ...(validation.warnings || [])].map((item, i) => (
                      <div key={i} className={`text-xs px-3 py-1.5 rounded ${item.severity === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                        {item.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-400">Run validation to check activity record quality.</p>
        )}
      </div>

      {/* Anomaly Detection */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-500" /> Anomaly Detection
          </h3>
          <button onClick={detectAnomalies} disabled={loading.anomalies} data-testid="detect-anomalies-btn"
            className="flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 text-slate-600 text-xs rounded-lg hover:border-slate-300 disabled:opacity-60">
            <RefreshCw size={11} className={loading.anomalies ? "animate-spin" : ""} /> Detect Anomalies
          </button>
        </div>
        {anomalies ? (
          anomalies.count === 0
            ? <p className="text-xs text-emerald-600 bg-emerald-50 px-3 py-2 rounded-lg">No anomalies detected.</p>
            : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">{anomalies.count} anomaly(-ies) — highest severity: <span className={`font-semibold ${anomalies.highest_severity === "high" ? "text-red-600" : "text-amber-600"}`}>{anomalies.highest_severity}</span></p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {(anomalies.anomalies || []).map((a, i) => (
                    <div key={i} className={`text-xs px-3 py-2 rounded border ${a.severity === "high" ? "bg-red-50 border-red-200 text-red-700" : a.severity === "medium" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                      <p className="font-medium">{a.description}</p>
                      <p className="mt-0.5 opacity-75">{a.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            )
        ) : (
          <p className="text-xs text-slate-400">Detect anomalies to identify data inconsistencies.</p>
        )}
      </div>

      {/* Verification Ladder */}
      <VerificationLadder assessmentId={assessmentId} currentScore={score?.overall_score} />
    </div>
  );
}
